import { useState, useCallback } from "react";
import { useCreate, useNotify, useRefresh } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { X, Plus, Loader2, GitBranch } from "lucide-react";

interface ChangeOrderFormProps {
  open: boolean;
  onClose: () => void;
  jobId: string;
  contactId?: string | number;
  currentTotal?: number;
}

export function ChangeOrderForm({ open, onClose, jobId, contactId, currentTotal = 0 }: ChangeOrderFormProps) {
  const [create] = useCreate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    title: "",
    description: "",
    reason: "",
    cost_change: 0,
    schedule_impact_days: 0,
  });

  const handleCreate = useCallback(async () => {
    if (!form.title.trim()) {
      notify("Title is required", { type: "warning" });
      return;
    }
    setSaving(true);
    try {
      const result = await create(
        "change_orders",
        {
          data: {
            job_id: jobId,
            contact_id: contactId || null,
            title: form.title.trim(),
            description: form.description.trim(),
            reason: form.reason.trim(),
            cost_change: form.cost_change,
            new_total: currentTotal + form.cost_change,
            schedule_impact_days: form.schedule_impact_days,
          },
        },
        { returnPromise: true }
      );

      await create("events", {
        data: {
          contact_id: contactId || null,
          job_id: jobId,
          event_type: "change_order_created",
          payload: {
            co_id: (result as { data?: { id?: string } })?.data?.id,
            title: form.title.trim(),
            cost_change: form.cost_change,
            schedule_impact_days: form.schedule_impact_days,
          },
        },
      }, { returnPromise: true });

      notify("Change order created", { type: "success" });
      refresh();
      onClose();
      setForm({ title: "", description: "", reason: "", cost_change: 0, schedule_impact_days: 0 });
    } catch (err) {
      notify(`Failed: ${(err as Error).message}`, { type: "error" });
    } finally {
      setSaving(false);
    }
  }, [form, jobId, contactId, currentTotal, create, notify, refresh, onClose]);

  if (!open) return null;

  const newTotal = currentTotal + form.cost_change;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Card className="w-full max-w-md mx-4 shadow-xl">
        <CardContent className="p-0">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-base font-semibold flex items-center gap-2">
              <GitBranch className="w-4 h-4" /> Change Order
            </h3>
            <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-4 space-y-3">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Title *</label>
              <input type="text" value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Add bathroom tile upgrade"
                className="w-full px-3 py-2 border rounded text-sm" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Description</label>
              <textarea value={form.description} rows={2}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                className="w-full px-3 py-2 border rounded text-sm resize-none" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Reason</label>
              <input type="text" value={form.reason}
                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                placeholder="e.g. Customer requested upgrade"
                className="w-full px-3 py-2 border rounded text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Cost change ($)</label>
                <input type="number" step={0.01} value={form.cost_change}
                  onChange={(e) => setForm((f) => ({ ...f, cost_change: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border rounded text-sm text-right" />
                <p className="text-xs text-muted-foreground mt-0.5">
                  New total: <span className="font-medium">${newTotal.toLocaleString()}</span>
                </p>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Schedule impact (days)</label>
                <input type="number" min={0} value={form.schedule_impact_days}
                  onChange={(e) => setForm((f) => ({ ...f, schedule_impact_days: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border rounded text-sm" />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 p-4 border-t bg-gray-50 rounded-b-lg">
            <button onClick={onClose} disabled={saving}
              className="px-3 py-1.5 rounded text-sm font-medium border text-gray-700 hover:bg-gray-100">
              Cancel
            </button>
            <button onClick={handleCreate} disabled={saving || !form.title.trim()}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Create CO
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
